import { makeStyles, Button, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import React from 'react'
import { formatCurrency, getObjectLabels } from '@paintscout/util/builder'
import { WisetackDisclaimerContent } from '../WisetackPromoDisclaimer'
import type { WisetackPromoResponse } from '../types'
import { useWisetack } from '../useWisetack'

const useStyles = makeStyles((theme: Theme) => ({
  textWrapper: {
    textAlign: 'left'
  },
  textSpacing: {
    marginBottom: theme.spacing(2)
  },
  disclaimer: {
    fontStyle: 'italic',
    textAlign: 'center'
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  }
}))

export interface WisetackLearnMoreProps {
  handleGetLoanApplication: () => Promise<void>
  isCustomerView?: boolean
  wisetackPromo?: WisetackPromoResponse
  // Compliance (ENG-833): only show maxAmountDisclosure when the job total exceeds the
  // merchant's max loan amount. Computed in WisetackPromo where the quote total is available.
  showMaxAmountDisclosure?: boolean
  // Merchant preview: render customer-facing content but keep the financing button inert.
  isPreview?: boolean
}

export default function WisetackLearnMore({
  handleGetLoanApplication,
  isCustomerView,
  wisetackPromo,
  showMaxAmountDisclosure,
  isPreview
}: WisetackLearnMoreProps) {
  const classes = useStyles()
  const content = isCustomerView ? (
    <CustomerViewContent
      handleGetLoanApplication={handleGetLoanApplication}
      wisetackPromo={wisetackPromo}
      showMaxAmountDisclosure={showMaxAmountDisclosure}
      isPreview={isPreview}
    />
  ) : (
    <MerchantContent />
  )
  return (
    <>
      {content}
      <div className={classes.disclaimer}>
        <WisetackDisclaimerContent wisetackPromo={wisetackPromo} />
      </div>
    </>
  )
}

const MerchantContent = () => {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { quote: quoteLabel } = getObjectLabels({ options, invoice: false })
  const { preferences, user } = useUser()
  const { maximumLoanAmount } = useWisetack({ options, user, preferences })
  return (
    <div className={classes.textWrapper}>
      <Typography variant="h6" className={classes.textSpacing}>
        How-to
      </Typography>
      <Typography className={classes.textSpacing}>
        Financing Application Process in 3 Easy Steps:
        <ol>
          <li>
            Send a financing application (or a qualifying {quoteLabel.value.toLowerCase()}) to your customer (no fee to
            you or impact to their credit score)
          </li>
          <li>
            Once your customer is approved and they’ve locked in an offer, complete the job and get paid from Wisetack!
          </li>
        </ol>
      </Typography>
      <Typography variant="h6" className={classes.textSpacing}>
        Steps for your Customer
      </Typography>
      <Typography className={classes.textSpacing}>
        Apply in less than 60 seconds and get approved for up to{' '}
        {formatCurrency({ value: maximumLoanAmount, options, abbreviationPostfix: '' })} with no credit score impact!
        Select the monthly payment that works best within the budget. Accept the terms and mark the work as complete.
      </Typography>
    </div>
  )
}

const CustomerViewContent = ({
  handleGetLoanApplication,
  wisetackPromo,
  showMaxAmountDisclosure,
  isPreview
}: {
  handleGetLoanApplication: () => Promise<void>
  wisetackPromo: WisetackPromoResponse
  showMaxAmountDisclosure?: boolean
  isPreview?: boolean
}) => {
  const classes = useStyles()
  return (
    <div className={classes.textWrapper}>
      <Typography variant="h6" className={classes.textSpacing}>
        {wisetackPromo.headline}
      </Typography>
      <Typography className={classes.textSpacing}>{wisetackPromo.tagline}</Typography>
      {/* Compliance (ENG-833): 1-click disclosure. When the job total exceeds the merchant's
          max loan amount, Wisetack sends maxAmountDisclosure to clarify the monthly payment is
          an example based on the financeable amount, not the job total. Sits between the tagline
          and the value props per Wisetack's mockup. Only render when present. */}
      {showMaxAmountDisclosure && wisetackPromo.maxAmountDisclosure && (
        <Typography className={classes.textSpacing}>{wisetackPromo.maxAmountDisclosure}</Typography>
      )}
      <Typography className={classes.textSpacing} component="div">
        <ul>
          <li>{wisetackPromo.valueProp1}</li>
          <li>{wisetackPromo.valueProp2}</li>
          <li>{wisetackPromo.valueProp3}</li>
        </ul>
      </Typography>
      <div className={classes.buttonWrapper}>
        {/* In the merchant preview the button is shown for fidelity but disabled so it can't
            initiate financing (ENG-833). */}
        <Button disabled={isPreview} onClick={handleGetLoanApplication}>
          {wisetackPromo.button}
        </Button>
      </div>
    </div>
  )
}
